import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  KeyRound,
  Loader2,
  ShieldCheck,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import Button from './ui/Button';
import Card from './ui/Card';
import InputField from './ui/InputField';

function Login() {
  const [assignedId, setAssignedId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Clear Horizons - Login';
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      setError('We could not verify those credentials. Please check your assigned ID and password.');
    }, 1100);
  };

  const handleJoinClick = () => {
    navigate('/home', { state: { scrollToBottom: true } });
  };

  return (
    <main id="main-content" tabIndex="-1" className="page-shell focus:outline-none">
      <section className="container-page grid min-h-[calc(100vh-7rem)] items-center gap-10 pb-20 pt-8 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="eyebrow">Secure access</p>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
            Sign in to your <span className="gradient-text">Clear Horizons</span> workspace.
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600">
            Candidate access is designed to stay simple and focused, with a polished form that
            gives clear feedback and works across devices.
          </p>

          <Card className="mt-8 overflow-hidden p-5">
            <div className="-mx-5 -mt-5 mb-5 h-2 bg-gradient-to-r from-blue-500 via-teal-400 to-amber-400" />
            <div className="flex gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-teal-50 text-blue-600">
                <BriefcaseBusiness className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-950">New to the mission?</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Reach out to the team to discuss profiles, employer partnerships, or community
                  support.
                </p>
                <button
                  type="button"
                  onClick={handleJoinClick}
                  className="focus-ring mt-3 inline-flex items-center gap-2 rounded-lg text-sm font-semibold text-blue-600 transition hover:text-blue-700"
                >
                  Contact us <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.08 }}
        >
          <Card className="animated-border mx-auto max-w-xl overflow-hidden bg-white p-1">
            <div className="overflow-hidden rounded-[calc(1rem-1px)] bg-white">
            <div className="border-b border-slate-200 bg-gradient-to-r from-slate-950 via-blue-800 to-teal-700 px-6 py-6 text-white sm:px-8">
              <div className="flex items-center gap-3">
                <BrandLogo className="h-12 w-12 rounded-xl bg-white" />
                <div>
                  <h2 className="text-lg font-semibold">Welcome back</h2>
                  <p className="text-sm text-blue-100">Use your assigned candidate credentials.</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-xl border border-white/10 bg-white/10 p-3">
                  <ShieldCheck className="mb-2 h-4 w-4 text-emerald-200" />
                  Accessible by design
                </div>
                <div className="rounded-xl border border-white/10 bg-white/10 p-3">
                  <Sparkles className="mb-2 h-4 w-4 text-amber-200" />
                  Profile-ready flow
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-6 sm:p-8">
              {error && (
                <div
                  className="flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-700"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <InputField
                id="assigned-id"
                label="Assigned ID"
                type="text"
                placeholder="Enter your assigned ID"
                aria-label="Assigned ID"
                value={assignedId}
                onChange={(event) => setAssignedId(event.target.value)}
                required
                icon={UserRound}
              />

              <InputField
                id="password"
                label="Password"
                type="password"
                placeholder="Enter your password"
                aria-label="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                icon={KeyRound}
              />

              <Button as="button" type="submit" className="w-full" size="lg" variant="color" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking credentials
                  </>
                ) : (
                  'Log in'
                )}
              </Button>
            </form>
            <div className="border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8">
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Built with polished states for loading, errors, and keyboard focus.
              </div>
            </div>
            </div>
          </Card>
        </motion.div>
      </section>
    </main>
  );
}

export default Login;
